import { validateObservedRepositoryOrigin } from './session-binding-validation';

export const SESSION_BINDING_ACTIVE_PROVIDERS = [
  'azure-devops',
  'github',
] as const;

export type SessionBindingProvider =
  (typeof SESSION_BINDING_ACTIVE_PROVIDERS)[number];

export const AI_SESSION_BINDING_STATUSES = [
  'active',
  'quarantined',
  'revoked',
  'expired',
  'closed',
] as const;

export type AiSessionBindingStatus =
  (typeof AI_SESSION_BINDING_STATUSES)[number];

export const SESSION_BINDING_RUNTIME_KINDS = [
  'claude',
  'codex',
  'mcp',
] as const;

export type SessionBindingRuntimeKind =
  (typeof SESSION_BINDING_RUNTIME_KINDS)[number];

export const SESSION_BINDING_REVALIDATION_CHECKPOINTS = [
  'session-start',
  'first-report',
  'batch-send',
  'pre-close',
] as const;

export type SessionBindingRevalidationCheckpoint =
  (typeof SESSION_BINDING_REVALIDATION_CHECKPOINTS)[number];

export const SESSION_BINDING_REASON_CODES = [
  'BINDING_MISSING',
  'BINDING_ACTIVITY_MISMATCH',
  'BINDING_EXPIRED',
  'BINDING_REVOKED',
  'ORIGIN_INVALID',
  'ORIGIN_MISMATCH',
  'FORK_NOT_SUPPORTED',
  'ASSET_MISSING',
  'ASSET_INCOMPATIBLE',
  'BUNDLE_INCOMPATIBLE',
  'CONTROL_PLANE_UNAVAILABLE',
  'SESSION_CLOSED',
] as const;

export type SessionBindingReasonCode =
  (typeof SESSION_BINDING_REASON_CODES)[number];

export const SESSION_BINDING_ERROR_CODES = [
  'SESSION_BINDING_INVALID_REQUEST',
  'SESSION_BINDING_UNAUTHORIZED',
  'SESSION_BINDING_FORBIDDEN',
  'SESSION_BINDING_NOT_FOUND',
  'SESSION_BINDING_CONFLICT',
  'SESSION_BINDING_EXPIRED',
  'SESSION_BINDING_ORIGIN_INVALID',
  'SESSION_BINDING_REQUIRED',
  'SESSION_BINDING_BUNDLE_INCOMPATIBLE',
  'SESSION_BINDING_UNAVAILABLE',
  'SESSION_CLOSURE_INVALID',
] as const;

export type SessionBindingErrorCode =
  (typeof SESSION_BINDING_ERROR_CODES)[number];

export const SESSION_BINDING_TTL_HOURS = 20;
export const SESSION_BINDING_REMOTE_MAX_LENGTH = 2048;
export const SESSION_BINDING_BRANCH_MAX_LENGTH = 255;
export const SESSION_BINDING_CLIENT_SESSION_ID_MAX_LENGTH = 255;
export const SESSION_BINDING_BUNDLE_VERSION_MAX_LENGTH = 128;
export const SESSION_SIGNAL_BATCH_MAX_ITEMS = 100;

export interface RepositoryIdentity {
  provider: SessionBindingProvider;
  owner: string | null;
  project: string | null;
  repository: string;
}

export interface ObservedRepositoryOrigin extends RepositoryIdentity {
  branch: string | null;
  revision: string;
  isFork: boolean;
}

export interface SessionBindingContext {
  bindingId: string;
  activityId: string;
  clientSessionId: string;
  origin: ObservedRepositoryOrigin;
  observedAt: string;
}

export const SESSION_SIGNAL_CONDITIONS = [
  'accepted',
  'rejected',
  'not_observed',
  'stale',
] as const;

export type SessionSignalCondition = (typeof SESSION_SIGNAL_CONDITIONS)[number];

export interface SessionSignalHealthProjection {
  condition: SessionSignalCondition;
  reasonCode: SessionBindingReasonCode | null;
  observedAt: string | null;
  /** Present only when a failed refresh preserves earlier evidence. */
  previousCondition?: Exclude<SessionSignalCondition, 'stale'>;
}

export interface CreateSessionBindingRequest {
  activityId: string;
  clientSessionId: string;
  runtimeKind: SessionBindingRuntimeKind;
  origin: ObservedRepositoryOrigin;
  bundleVersion: string;
  assetManifestDigest: string;
}

export interface RevalidateSessionBindingRequest {
  checkpoint: SessionBindingRevalidationCheckpoint;
  origin: ObservedRepositoryOrigin;
}

export interface AiSessionBindingProjection {
  id: string;
  activityId: string;
  agentId: string;
  clientSessionId: string;
  runtimeKind: SessionBindingRuntimeKind;
  repository: RepositoryIdentity;
  branchAtIssue: string | null;
  revisionAtIssue: string;
  assetManifestDigest: string;
  status: AiSessionBindingStatus;
  reasonCode: SessionBindingReasonCode | null;
  issuedAt: string;
  expiresAt: string;
  lastValidatedAt: string;
  createdAt: string;
  updatedAt: string;
  /** Non-null exactly when the binding is `closed` or `expired` (ADR-005). */
  closureOutcome: SessionClosureOutcome | null;
  closedAt: string | null;
  closureCheckpoint: SessionClosureCheckpoint | null;
}

/**
 * Why a session stopped being observable. Every terminated binding carries one
 * so an absence of usage always has a cause on the record (ADR-005).
 */
export const SESSION_CLOSURE_OUTCOMES = [
  'USAGE_CONFIRMED',
  'USAGE_CONFIRMED_LATE',
  'USAGE_PENDING_DELIVERY',
  'NO_USAGE_OBSERVED',
  'TELEMETRY_INCOMPATIBLE',
  'CLOSE_UNCONFIRMED',
] as const;

export type SessionClosureOutcome = (typeof SESSION_CLOSURE_OUTCOMES)[number];

/**
 * Which local signal closed the session. `sweeper` is the platform's own
 * fallback and is never accepted from a client.
 */
export const SESSION_CLOSURE_CHECKPOINTS = [
  'session-end',
  'pre-close',
  'supervisor-stopped',
  'sweeper',
] as const;

export type SessionClosureCheckpoint =
  (typeof SESSION_CLOSURE_CHECKPOINTS)[number];

export const SESSION_CLOSURE_CLIENT_CHECKPOINTS = [
  'session-end',
  'pre-close',
  'supervisor-stopped',
] as const;

export type SessionClosureClientCheckpoint =
  (typeof SESSION_CLOSURE_CLIENT_CHECKPOINTS)[number];

export const SESSION_CLOSURE_MAX_CAPABILITIES = 32;

export interface SessionClosureRequest {
  context: SessionBindingContext;
  checkpoint: SessionClosureClientCheckpoint;
  capabilities: string[];
  observedAt: string;
}

export interface SessionClosureResponse {
  outcome: SessionClosureOutcome;
  replayed: boolean;
}

export function isSessionClosureOutcome(
  value: unknown,
): value is SessionClosureOutcome {
  return SESSION_CLOSURE_OUTCOMES.includes(value as SessionClosureOutcome);
}

export function isSessionClosureCheckpoint(
  value: unknown,
): value is SessionClosureCheckpoint {
  return SESSION_CLOSURE_CHECKPOINTS.includes(
    value as SessionClosureCheckpoint,
  );
}

/**
 * Exact validator: an unknown key is a refusal, not something to ignore, so a
 * bundle that starts sending an unmodelled field is caught at the boundary.
 */
export function isSessionClosureRequest(
  value: unknown,
): value is SessionClosureRequest {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  const allowed = ['context', 'checkpoint', 'capabilities', 'observedAt'];
  return (
    Object.keys(record).every(key => allowed.includes(key)) &&
    isSessionBindingContext(record.context) &&
    SESSION_CLOSURE_CLIENT_CHECKPOINTS.includes(
      record.checkpoint as SessionClosureClientCheckpoint,
    ) &&
    isCapabilityList(record.capabilities) &&
    typeof record.observedAt === 'string' &&
    Number.isFinite(Date.parse(record.observedAt))
  );
}

function isCapabilityList(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length <= SESSION_CLOSURE_MAX_CAPABILITIES &&
    value.every(
      item => typeof item === 'string' && /^[A-Za-z0-9._:-]{1,64}$/.test(item),
    )
  );
}

export class SessionBindingContractError extends Error {
  constructor(readonly reasonCode: SessionBindingReasonCode, message: string) {
    super(message);
    this.name = 'SessionBindingContractError';
  }
}

export {
  isCreateSessionBindingRequest,
  normalizeRepositoryRemote,
  parseCreateSessionBindingRequest,
  parseRevalidateSessionBindingRequest,
} from './session-binding-validation';
export { validateObservedRepositoryOrigin } from './session-binding-validation';

export function sameRepositoryIdentity(
  expected: RepositoryIdentity,
  observed: RepositoryIdentity,
): boolean {
  return (
    expected.provider === observed.provider &&
    expected.owner === observed.owner &&
    expected.project === observed.project &&
    expected.repository === observed.repository
  );
}

export function isSessionBindingContext(
  value: unknown,
): value is SessionBindingContext {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  if (
    Object.keys(record).some(
      key =>
        ![
          'bindingId',
          'activityId',
          'clientSessionId',
          'origin',
          'observedAt',
        ].includes(key),
    ) ||
    !uuid(record.bindingId) ||
    !boundedActivityIdentifier(record.activityId) ||
    !boundedSessionIdentifier(record.clientSessionId) ||
    typeof record.observedAt !== 'string' ||
    !Number.isFinite(Date.parse(record.observedAt))
  ) {
    return false;
  }
  try {
    validateObservedRepositoryOrigin(record.origin);
    return true;
  } catch {
    return false;
  }
}

function uuid(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      value,
    )
  );
}

function boundedSessionIdentifier(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9._:-]{1,255}$/.test(value);
}

function boundedActivityIdentifier(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9@._:+/-]{1,200}$/.test(value);
}

export function isSessionBindingCurrent(
  binding: Pick<
    AiSessionBindingProjection,
    'status' | 'issuedAt' | 'expiresAt'
  >,
  now: string,
): boolean {
  const issuedAt = Date.parse(binding.issuedAt);
  const expiresAt = Date.parse(binding.expiresAt);
  const current = Date.parse(now);
  return (
    binding.status === 'active' &&
    Number.isFinite(issuedAt) &&
    Number.isFinite(expiresAt) &&
    Number.isFinite(current) &&
    issuedAt <= current &&
    issuedAt <= expiresAt &&
    current < expiresAt &&
    expiresAt - issuedAt <= SESSION_BINDING_TTL_HOURS * 60 * 60 * 1000
  );
}

export function classifyLegacyBindingState(input: unknown): {
  status: 'quarantined';
  reasonCode: 'BINDING_MISSING' | 'ORIGIN_INVALID';
  remoteMutationAllowed: false;
} {
  return {
    status: 'quarantined',
    reasonCode:
      input === null || input === undefined
        ? 'BINDING_MISSING'
        : 'ORIGIN_INVALID',
    remoteMutationAllowed: false,
  };
}

export const SESSION_BINDING_REPOSITORY_FIXTURES = [
  {
    remotes: [
      'https://github.com/Spassu/Control-Plane.git',
      'git@github.com:spassu/control-plane.git',
      'ssh://git@github.com/spassu/control-plane.git',
    ],
    identity: {
      provider: 'github',
      owner: 'spassu',
      project: null,
      repository: 'control-plane',
    },
  },
  {
    remotes: [
      'https://dev.azure.com/Spassu/Platform/_git/Control-Plane',
      'git@ssh.dev.azure.com:v3/spassu/platform/control-plane',
      'ssh://git@ssh.dev.azure.com/v3/spassu/platform/control-plane',
    ],
    identity: {
      provider: 'azure-devops',
      owner: 'spassu',
      project: 'platform',
      repository: 'control-plane',
    },
  },
] as const;
